import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

interface JobForm {
  jobTitle: string;
  companyName: string;
  jobRole: string;
  eligibility: string;
}

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
  companyName: string;
  district: string;
  state: string;
  hasLaptop: string;
  appliedDate: string;
  resume?: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [adminEmail, setAdminEmail] = useState('');
  const [activeTab, setActiveTab] = useState<'postJob' | 'viewCandidates'>('postJob');
  const [authLoading, setAuthLoading] = useState(true);
  
  // Job Form State
  const [jobForm, setJobForm] = useState<JobForm>({
    jobTitle: '',
    companyName: '',
    jobRole: '',
    eligibility: ''
  });
  const [jobLoading, setJobLoading] = useState(false);
  const [jobSuccess, setJobSuccess] = useState('');
  const [jobError, setJobError] = useState('');

  // Candidates State
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);

  useEffect(() => {
    // Hide navbar and footer on mount
    const navbar = document.querySelector('nav');
    const footer = document.querySelector('footer');
    if (navbar) navbar.style.display = 'none';
    if (footer) footer.style.display = 'none';

    // Listen to auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const isAuthenticated = localStorage.getItem('isAdminAuthenticated');
      
      if (!user || !isAuthenticated) {
        navigate('/admin/login');
      } else {
        setAdminEmail(user.email || '');
        setAuthLoading(false);
      }
    });

    // Show navbar and footer on unmount
    return () => {
      unsubscribe();
      const navbar = document.querySelector('nav');
      const footer = document.querySelector('footer');
      if (navbar) navbar.style.display = '';
      if (footer) footer.style.display = '';
    };
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'viewCandidates' && !authLoading) {
      fetchCandidates();
    }
  }, [activeTab, authLoading]);

  const handleJobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setJobLoading(true);
    setJobError('');
    setJobSuccess('');

    try {
      await addDoc(collection(db, 'jobs'), {
        ...jobForm,
        postedBy: adminEmail,
        postedDate: Timestamp.now(),
        status: 'active'
      });

      setJobSuccess('Job posted successfully!');
      setJobForm({
        jobTitle: '',
        companyName: '',
        jobRole: '',
        eligibility: ''
      });

      setTimeout(() => setJobSuccess(''), 3000);
    } catch (error) {
      console.error('Error posting job:', error);
      setJobError('Failed to post job. Please try again.');
    } finally {
      setJobLoading(false);
    }
  };

  const fetchCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const candidatesQuery = query(
        collection(db, 'applications'),
        orderBy('appliedDate', 'desc')
      );
      const querySnapshot = await getDocs(candidatesQuery);
      
      const candidatesData: Candidate[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        candidatesData.push({
          id: doc.id,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          jobTitle: data.jobTitle || '',
          companyName: data.companyName || '',
          district: data.district || '',
          state: data.state || '',
          hasLaptop: data.hasLaptop || '',
          appliedDate: data.appliedDate?.toDate().toLocaleDateString() || '',
          resume: data.resume || ''
        });
      });

      setCandidates(candidatesData);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('isAdminAuthenticated');
      localStorage.removeItem('adminEmail');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-5xl text-primary mb-4"></i>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header with Logout */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <i className="fas fa-shield-alt text-primary mr-3"></i>
              Nexedz Admin Panel
            </h1>
            <p className="text-gray-600 mt-1">
              <i className="fas fa-user-circle mr-2"></i>
              {adminEmail}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-primary hover:bg-secondary text-white px-6 py-3 rounded-md font-medium transition duration-300 flex items-center shadow-lg"
          >
            <i className="fas fa-sign-out-alt mr-2"></i>
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('postJob')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm transition duration-300 ${
                  activeTab === 'postJob'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-briefcase mr-2"></i>
                Post Job
              </button>
              <button
                onClick={() => setActiveTab('viewCandidates')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm transition duration-300 ${
                  activeTab === 'viewCandidates'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-users mr-2"></i>
                View Candidates
              </button>
            </nav>
          </div>
        </div>

        {/* Post Job Tab */}
        {activeTab === 'postJob' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                <i className="fas fa-plus-circle text-primary mr-2"></i>
                Post a New Job
              </h2>
              <p className="text-gray-600">Fill in the details to post a new job opening</p>
            </div>

            {jobSuccess && (
              <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
                <i className="fas fa-check-circle mr-2"></i>
                {jobSuccess}
              </div>
            )}

            {jobError && (
              <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {jobError}
              </div>
            )}

            <form onSubmit={handleJobSubmit} className="space-y-6">
              <div>
                <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="jobTitle"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  placeholder="e.g., Senior Software Engineer"
                  value={jobForm.jobTitle}
                  onChange={(e) => setJobForm({ ...jobForm, jobTitle: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  placeholder="e.g., Tech Solutions Pvt Ltd"
                  value={jobForm.companyName}
                  onChange={(e) => setJobForm({ ...jobForm, companyName: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="jobRole" className="block text-sm font-medium text-gray-700 mb-2">
                  Job Role <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="jobRole"
                  required
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  placeholder="Describe the job responsibilities and duties..."
                  value={jobForm.jobRole}
                  onChange={(e) => setJobForm({ ...jobForm, jobRole: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="eligibility" className="block text-sm font-medium text-gray-700 mb-2">
                  Eligibility <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="eligibility"
                  required
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  placeholder="e.g., Bachelor's degree in Computer Science, 3+ years experience..."
                  value={jobForm.eligibility}
                  onChange={(e) => setJobForm({ ...jobForm, eligibility: e.target.value })}
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={jobLoading}
                  className="bg-primary hover:bg-secondary text-white px-6 py-3 rounded-md font-medium transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {jobLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Posting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane mr-2"></i>
                      Submit Job
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* View Candidates Tab */}
        {activeTab === 'viewCandidates' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                <i className="fas fa-user-graduate text-primary mr-2"></i>
                Job Applications
              </h2>
              <p className="text-gray-600">View and manage candidate applications</p>
            </div>

            {candidatesLoading ? (
              <div className="flex justify-center items-center p-12">
                <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center p-12">
                <i className="fas fa-inbox text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg">No applications yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Applied
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Laptop
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Applied Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidates.map((candidate) => (
                      <tr key={candidate.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                                <span className="text-white font-medium">
                                  {candidate.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{candidate.email}</div>
                          <div className="text-sm text-gray-500">{candidate.phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{candidate.district}</div>
                          <div className="text-sm text-gray-500">{candidate.state}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{candidate.jobTitle}</div>
                          <div className="text-sm text-gray-500">{candidate.companyName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            candidate.hasLaptop === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {candidate.hasLaptop}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{candidate.appliedDate}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {candidate.resume && (
                            <a
                              href={candidate.resume}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:text-secondary mr-3 inline-block"
                            >
                              <i className="fas fa-file-pdf mr-1"></i>
                              Resume
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;